import { compileDashboardFeature, DashboardComponentType } from '@/features/dashboard'
import { EntityLevel } from '@/composables/useEntityLevel'
import { formatPercentRounded } from '@/features/analytics/formats'

const enabledEntityLevels: EntityLevel[] = [
  'COMPLEX',
  'REAL_ESTATE',
  'LEVEL',
  'SPACE',
  'FLOOR',
  'CONSTRUCTION'
]

const meta = {
  numberOfSpaces: {
    field: 'count'
  }
}

export const utilizationRate = compileDashboardFeature({
  name: 'utilization-rate',
  title: {
    sv: 'Nyttjande&shy;grad',
    en: 'Utilization Rate'
  },
  dataSectionTitle: {
    sv: 'Nyttjandegrad',
    en: 'Utilization Rate'
  },
  icon: 'IconClockRefresh',
  enabled: true,
  defaultFilters: {
    calendar: {
      quickSelect: 'last30days'
    },
    defaultDateSelection: 'last30d',
    onlyBusinessDays: true,
    onlyBusinessHours: true,
    onlyBusinessHoursFilterType: 'query',
    filterTags: ['space_classification_type', 'bookable', 'number_of_seats_tag'],
    dateSelections: [
      'now',
      'last24h',
      'yesterday',
      'last7d',
      'last30d',
      'lastquarter',
      'last12m',
      'currentyear',
      'lastyear',
      'custom'
    ]
  },
  measurements: [
    {
      name: 'utilization',
      field: {
        name: 'usage',
        expression: {
          type: 'avg',
          exp: {
            type: 'field',
            name: 'used'
          }
        }
      },
      title: {
        sv: 'Användning',
        en: 'Usage'
      },
      options: {
        color: '#40A9FF',
        enabledEntityLevels,
        format: formatPercentRounded,
        min: 0,
        max: 1
      }
    },
    {
      name: 'utilization',
      field: {
        name: 'utilization_rate',
        expression: {
          type: 'div',
          exps: [
            {
              type: 'sum',
              exp: {
                type: 'field',
                name: 'number_of_people'
              }
            },
            {
              type: 'null_if',
              value: {
                type: 'sum',
                exp: {
                  type: 'field',
                  name: 'number_of_seats'
                }
              },
              nullIfEqual: {
                type: 'number',
                value: 0
              }
            }
          ]
        }
      },
      title: {
        sv: 'Nyttjandegrad',
        en: 'Utilization Rate'
      },
      options: {
        color: '#0050B3',
        enabledEntityLevels,
        format: formatPercentRounded,
        min: 0,
        max: 1
      }
    },
    {
      name: 'utilization',
      field: {
        name: 'used_booked',
        expression: {
          type: 'avg',
          exp: {
            type: 'mul',
            exps: [
              { type: 'field', name: 'used' },
              { type: 'field', name: 'booked' }
            ]
          }
        }
      },
      title: {
        sv: 'Använd, bokad',
        en: 'Used, booked'
      },
      options: {
        color: '#FFC53D',
        enabledEntityLevels,
        format: formatPercentRounded,
        min: 0,
        max: 1
      }
    },
    {
      name: 'utilization',
      field: {
        name: 'utilization_rate_used_booked',
        expression: {
          type: 'div',
          exps: [
            {
              type: 'sum',
              exp: {
                type: 'mul',
                exps: [
                  {
                    type: 'field',
                    name: 'number_of_people'
                  },
                  {
                    type: 'field',
                    name: 'used'
                  },
                  {
                    type: 'field',
                    name: 'booked'
                  }
                ]
              }
            },
            {
              type: 'null_if',
              value: {
                type: 'sum',
                exp: {
                  type: 'mul',
                  exps: [
                    {
                      type: 'field',
                      name: 'number_of_seats'
                    },
                    { type: 'field', name: 'used' },
                    {
                      type: 'field',
                      name: 'booked'
                    }
                  ]
                }
              },
              nullIfEqual: {
                type: 'number',
                value: 0
              }
            }
          ]
        }
      },
      title: {
        sv: 'Nyttjandegrad använd, bokad',
        en: 'Utilization Rate used, booked'
      },
      options: {
        color: '#AD6800',
        enabledEntityLevels,
        format: formatPercentRounded,
        min: 0,
        max: 1
      }
    },
    {
      name: 'utilization',
      field: {
        name: 'used_not_booked',
        expression: {
          type: 'avg',
          exp: {
            type: 'mul',
            exps: [
              { type: 'field', name: 'used' },
              {
                type: 'sub',
                exps: [
                  { type: 'number', value: 1 },
                  { type: 'field', name: 'booked' }
                ]
              }
            ]
          }
        }
      },
      title: {
        sv: 'Använd, ej bokad',
        en: 'Used, not booked'
      },
      options: {
        color: '#36CFC9',
        enabledEntityLevels,
        format: formatPercentRounded,
        min: 0,
        max: 1
      }
    },
    {
      name: 'utilization',
      field: {
        name: 'utilization_rate_used_not_booked',
        expression: {
          type: 'div',
          exps: [
            {
              type: 'avg',
              exp: {
                type: 'mul',
                exps: [
                  { type: 'field', name: 'number_of_people' },
                  { type: 'field', name: 'used' },
                  {
                    type: 'sub',
                    exps: [
                      { type: 'number', value: 1 },
                      { type: 'field', name: 'booked' }
                    ]
                  }
                ]
              }
            },
            {
              type: 'null_if',
              value: {
                type: 'avg',
                exp: {
                  type: 'mul',
                  exps: [
                    { type: 'field', name: 'number_of_seats' },
                    { type: 'field', name: 'used' },
                    {
                      type: 'sub',
                      exps: [
                        { type: 'number', value: 1 },
                        { type: 'field', name: 'booked' }
                      ]
                    }
                  ]
                }
              },
              nullIfEqual: {
                type: 'number',
                value: 0
              }
            }
          ]
        }
      },
      title: {
        sv: 'Nyttjandegrad använd, ej bokad',
        en: 'Utilization Rate used, not booked'
      },
      options: {
        color: '#006D75',
        enabledEntityLevels,
        format: formatPercentRounded,
        min: 0,
        max: 1
      }
    },
    {
      name: 'utilization',
      field: {
        name: 'count',
        expression: {
          type: 'count',
          exp: { type: 'field', name: 'entity_id' },
          distinct: true
        }
      },
      title: {
        sv: 'Antal rum',
        en: 'Space count'
      },
      options: {
        enabledEntityLevels: ['SPACE'],
        format: {
          format: {
            type: 'intl-number',
            maximumFractionDigits: 0
          }
        }
      }
    },
    {
      name: 'utilization_rate_histogram',
      field: {
        name: 'utilization_rate_histogram',
        expression: {
          type: 'histogram',
          min: 0,
          max: 1,
          nbuckets: 10,
          exp: {
            type: 'field',
            name: 'utilization_rate_histogram'
          }
        }
      },
      title: {
        sv: 'Fördelning av nyttjandegrad för antal timmar',
        en: 'Distribution of utilization for number of hours'
      },
      options: {
        colorScale: {
          type: 'SINGLE',
          cutoffs: [
            {
              color: '#003A8C'
            }
          ]
        },
        enabledEntityLevels: enabledEntityLevels,
        format: formatPercentRounded
      }
    },
    {
      name: 'used_booked_histogram',
      field: {
        name: 'used_booked_histogram',
        expression: {
          type: 'histogram',
          min: 0,
          max: 1,
          nbuckets: 10,
          exp: {
            type: 'field',
            name: 'used_booked_histogram'
          }
        }
      },
      title: {
        sv: 'Fördelning av nyttjandegrad för antal timmar',
        en: 'Distribution of utilization for number of hours'
      },
      options: {
        colorScale: {
          type: 'SINGLE',
          cutoffs: [
            {
              color: '#AD6800'
            }
          ]
        },
        enabledEntityLevels: enabledEntityLevels,
        format: formatPercentRounded
      }
    },
    {
      name: 'used_not_booked_histogram',
      field: {
        name: 'used_not_booked_histogram',
        expression: {
          type: 'histogram',
          min: 0,
          max: 1,
          nbuckets: 10,
          exp: {
            type: 'field',
            name: 'used_not_booked_histogram'
          }
        }
      },
      title: {
        sv: 'Fördelning av nyttjandegrad för antal timmar',
        en: 'Distribution of utilization for number of hours'
      },
      options: {
        colorScale: {
          type: 'SINGLE',
          cutoffs: [
            {
              color: '#006D75'
            }
          ]
        },
        enabledEntityLevels: enabledEntityLevels,
        format: formatPercentRounded
      }
    }
  ],
  components: [
    {
      type: DashboardComponentType.Kpi,
      name: 'used-utilization-rate',
      measurements: ['utilization.usage', 'utilization.count'],
      meta,
      info: {
        sv: 'Hur stor del av totalt tillgängliga timmar som är använda.',
        en: 'How much of the total available hours are used.'
      },
      details: {
        groups: ['space_classification_type'],
        fields: ['count', 'usage']
      }
    },
    {
      type: DashboardComponentType.Kpi,
      name: 'utilization_rate-utilization-rate',
      measurements: ['utilization.utilization_rate', 'utilization.count'],
      meta,
      info: {
        sv: 'Visar hur väl tillgängliga platser nyttjas per rum under allt tillgänglig tid.',
        en: 'Shows how well available spaces are utilized per room during all available time.'
      },
      measurementOverride: {
        options: {
          color: '#0050B3'
        }
      },
      details: {
        groups: ['space_classification_type'],
        fields: ['count', 'utilization_rate']
      }
    },
    {
      type: DashboardComponentType.Kpi,
      name: 'utilization_rate_used_booked-utilization-rate',
      measurements: ['utilization.utilization_rate_used_booked', 'utilization.count'],
      meta,
      info: {
        sv: 'Visar hur nyttjandegraden är i rummen när dom är använda och bokade. Indikerar hur väl storlek på rum matchar verksamhetens behov.',
        en: 'Shows the utilization rate in the rooms when they are used and booked. Indicates how well the size of the room matches the needs of the activity.'
      },
      measurementOverride: {
        options: {
          color: '#AD6800'
        }
      },
      details: {
        groups: ['space_classification_type'],
        fields: ['count', 'utilization_rate_used_booked']
      }
    },
    {
      type: DashboardComponentType.Kpi,
      name: 'used-booked-utilization',
      measurements: ['utilization.used_booked', 'utilization.count'],
      meta,
      info: {
        sv: 'Hur stor del av tiden som är bokad och använd.',
        en: 'How much of the time is booked and used.'
      },
      measurementOverride: {
        options: {
          color: '#FFC53D'
        }
      },
      details: {
        groups: ['space_classification_type'],
        fields: ['count', 'used_booked']
      }
    },
    {
      type: DashboardComponentType.Kpi,
      name: 'used-not-booked-utilization',
      measurements: ['utilization.used_not_booked', 'utilization.count'],
      meta,
      info: {
        sv: 'Hur stor del av tiden det är använt men ej bokad.',
        en: 'How much of the time is used but not booked.'
      },
      measurementOverride: {
        options: {
          color: '#36CFC9'
        }
      },
      details: {
        groups: ['space_classification_type'],
        fields: ['count', 'used_not_booked']
      }
    },
    {
      type: DashboardComponentType.Kpi,
      name: 'used_not_booked-utilization-rate',
      measurements: ['utilization.utilization_rate_used_not_booked', 'utilization.count'],
      meta,
      info: {
        sv: 'Visar hur nyttjandegraden är i rummen när dom är använda men ej bokade. Indikerar hur väl rummen fylls när det inte är schemalagd undervisning.',
        en: 'Shows the utilization rate in the rooms when they are used but not booked. Indicates how well the rooms are filled when there is no scheduled teaching.'
      },
      measurementOverride: {
        options: {
          color: '#006D75'
        }
      },
      details: {
        groups: ['space_classification_type'],
        fields: ['count', 'utilization_rate_used_not_booked']
      }
    },
    {
      type: DashboardComponentType.BarChart,
      name: 'utilization-rate-bar-chart',
      grouping: 'grouped',
      measurements: ['utilization.usage', 'utilization.utilization_rate'],
      groupKey: 'timestamp_extract_isodow',
      codomainTicks: {
        type: 'auto',
        minimumEnd: 1
      },
      measurementOverride: {
        options: {
          colorScale: null
        }
      },
      filters: {
        values: {
          query: {
            groupBy: [{ type: 'timestamp_extract', extract: 'ISODOW' }]
          }
        }
      },
      title: {
        sv: 'Nyttjandegrad över veckodagar',
        en: 'Utilization rate over weekdays'
      },
      info: {
        sv: 'Stapeldiagrammet visar vald typ av användning och nyttjandegrad aggregerat över veckodag. \n\n OBS: Användningen i detta diagram visas endast på rum med personräknare och kan således skilja sig från användningen i analysen ”Användning och Bokning” \n\n \nMätvärden:\n- Användning: Hur stor del av totalt tillgängliga timmar som är använda\n-Använd bokad: Hur stor del av tiden som är bokad och använd. \n- Använd ej bokad: Hur stor del av tiden det är använt men ej bokad. \n- Nyttjandegrad total tid: Visar hur väl tillgängliga platser nyttjas per rum under allt tillgänglig tid. \n- Nyttjandegrad bokad tid: Visar hur nyttjandegraden är i rummen när dom är använda och bokade. Indikerar hur väl storlek på rum matchar verksamhetens behov. \n- Nyttjandegrad ej bokad tid: Visar hur nyttjandegraden är i rummen när dom är använda men ej bokade. Indikerar hur väl rummen fylls när det inte är schemalagd undervisning.',
        en: 'The bar chart shows the selected type of usage and utilization rate aggregated over weekdays. \n\n Note: The usage in this chart is only shown for rooms with people counters and may therefore differ from the usage in the "Usage and Booking" analysis. \n\n \nMetrics:\n- Usage: The proportion of total available hours that are used\n- Used booked: The proportion of time that is booked and used. \n- Used not booked: The proportion of time that is used but not booked. \n- Utilization rate total time: Shows how well available seats are utilized per room during all available time. \n- Utilization rate booked time: Shows the utilization rate in the rooms when they are used and booked. Indicates how well the size of the room matches the needs of the activity. \n- Utilization rate not booked time: Shows how well the rooms are filled when there is no scheduled teaching.'
      }
    },
    {
      type: DashboardComponentType.BarChart,
      name: 'utilization-rate-bar-chart-hours',
      grouping: 'grouped',
      measurements: ['utilization.usage', 'utilization.utilization_rate'],
      groupKey: 'timestamp_extract_hour',
      codomainTicks: {
        type: 'auto',
        minimumEnd: 1
      },
      measurementOverride: {
        options: {
          colorScale: null
        }
      },
      filters: {
        values: {
          query: {
            groupBy: [{ type: 'timestamp_extract', extract: 'HOUR' }]
          }
        }
      },
      title: {
        sv: 'Nyttjandegrad över timmar',
        en: 'Utilization rate over hours'
      },
      info: {
        sv: 'Stapeldiagrammet visar vald typ av användning och nyttjandegrad aggregerat över timmar. \n\n OBS: Användningen i detta diagram visas endast på rum med personräknare och kan således skilja sig från användningen i analysen ”Användning och Bokning”. \n\n \nMätvärden:\n- Användning: Hur stor del av totalt tillgängliga timmar som är använda\n-Använd bokad: Hur stor del av tiden som är bokad och använd. \n- Använd ej bokad: Hur stor del av tiden det är använt men ej bokad. \n- Nyttjandegrad total tid: Visar hur väl tillgängliga platser nyttjas per rum under allt tillgänglig tid. \n- Nyttjandegrad bokad tid: Visar hur nyttjandegraden är i rummen när dom är använda och bokade. Indikerar hur väl storlek på rum matchar verksamhetens behov. \n- Nyttjandegrad ej bokad tid: Visar hur nyttjandegraden är i rummen när dom är använda men ej bokade. Indikerar hur väl rummen fylls när det inte är schemalagd undervisning.',
        en: 'The bar chart shows selected usage and utilization rate aggregated over hours. \n\n Note: The usage in this chart is only shown for rooms with people counters and may therefore differ from the usage in the "Usage and Booking" analysis. \n\n \nMetrics:\n- Usage: The proportion of total available hours that are used\n- Used booked: The proportion of time that is booked and used. \n- Used not booked: The proportion of time that is used but not booked. \n- Utilization rate total time: Shows how well available seats are utilized per room during all available time. \n- Utilization rate booked time: Shows the utilization rate in the rooms when they are used and booked. Indicates how well the size of the room matches the needs of the activity. \n- Utilization rate not booked time: Shows the utilization rate in the rooms when they are used but not booked. Indicates how well the rooms are filled when there is no scheduled teaching.'
      }
    },
    {
      type: DashboardComponentType.BarChart,
      name: 'utilization-rate-bar-chart-number-of-seats',
      grouping: 'grouped',
      measurements: ['utilization.usage', 'utilization.utilization_rate'],
      groupKey: 'width_bucket_number_of_seats',
      codomainTicks: {
        type: 'auto',
        minimumEnd: 1
      },
      measurementOverride: {
        options: {
          colorScale: null
        }
      },
      filters: {
        values: {
          query: {
            groupBy: [
              { type: 'width_bucket', min: 0, max: 100, count: 4, column: 'number_of_seats' }
            ]
          }
        }
      },
      title: {
        sv: 'Nyttjandegrad per antal platser',
        en: 'Utilization rate per number of seats'
      },
      info: {
        sv: 'Stapeldiagrammet visar vald typ av användning och nyttjandegrad aggregerat per antal platser. \n\n OBS: Användningen i detta diagram visas endast på rum med personräknare och kan således skilja sig från användningen i analysen ”Användning och Bokning”. \n\n \nMätvärden:\n- Användning: Hur stor del av totalt tillgängliga timmar som är använda\n-Använd bokad: Hur stor del av tiden som är bokad och använd. \n- Använd ej bokad: Hur stor del av tiden det är använt men ej bokad. \n- Nyttjandegrad total tid: Visar hur väl tillgängliga platser nyttjas per rum under allt tillgänglig tid. \n- Nyttjandegrad bokad tid: Visar hur nyttjandegraden är i rummen när dom är använda och bokade. Indikerar hur väl storlek på rum matchar verksamhetens behov. \n- Nyttjandegrad ej bokad tid: Visar hur nyttjandegraden är i rummen när dom är använda men ej bokade. Indikerar hur väl rummen fylls när det inte är schemalagd undervisning.',
        en: 'The bar chart shows selected usage and utilization rate aggregated per number of seats. \n\n Note: The usage in this chart is only shown for rooms with people counters and may therefore differ from the usage in the "Usage and Booking" analysis. \n\n \nMetrics:\n- Usage: The proportion of total available hours that are used\n- Used booked: The proportion of time that is booked and used. \n- Used not booked: The proportion of time that is used but not booked. \n- Utilization rate total time: Shows how well available seats are utilized per room during all available time. \n- Utilization rate booked time: Shows the utilization rate in the rooms when they are used and booked. Indicates how well the size of the room matches the needs of the activity. \n- Utilization rate not booked time: Shows the utilization rate in the rooms when they are used but not booked. Indicates how well the rooms are filled when there is no scheduled teaching.'
      }
    },
    {
      type: DashboardComponentType.TimeSeries,
      name: 'utilization-rate-time-series',
      measurements: ['utilization.usage', 'utilization.utilization_rate'],
      yAxisTicks: {
        type: 'auto',
        start: 0,
        minimumEnd: 1
      },
      title: {
        sv: 'Nyttjandegrad per tidsenhet',
        en: 'Utilization rate per time unit'
      },
      info: {
        sv: 'Grafen visar vald användning och nyttjandegrad över tid. \n\n OBS: Användningen i detta diagram visas endast på rum med personräknare och kan således skilja sig från användningen i analysen ”Användning och Bokning”. \n\n \nMätvärden:\n- Användning: Hur stor del av totalt tillgängliga timmar som är använda\n-Använd bokad: Hur stor del av tiden som är bokad och använd. \n- Använd ej bokad: Hur stor del av tiden det är använt men ej bokad. \n- Nyttjandegrad total tid: Visar hur väl tillgängliga platser nyttjas per rum under allt tillgänglig tid. \n- Nyttjandegrad bokad tid: Visar hur nyttjandegraden är i rummen när dom är använda och bokade. Indikerar hur väl storlek på rum matchar verksamhetens behov. \n- Nyttjandegrad ej bokad tid: Visar hur nyttjandegraden är i rummen när dom är använda men ej bokade. Indikerar hur väl rummen fylls när det inte är schemalagd undervisning.',
        en: 'The chart shows selected usage and utilization rate over time. \n\n Note: The usage in this chart is only shown for rooms with people counters and may therefore differ from the usage in the "Usage and Booking" analysis. \n\n \nMetrics:\n- Usage: The proportion of total available hours that are used\n- Used booked: The proportion of time that is booked and used. \n- Used not booked: The proportion of time that is used but not booked. \n- Utilization rate total time: Shows how well available seats are utilized per room during all available time. \n- Utilization rate booked time: Shows the utilization rate in the rooms when they are used and booked. Indicates how well the size of the room matches the needs of the activity. \n- Utilization rate not booked time: Shows the utilization rate in the rooms when they are used but not booked. Indicates how well the rooms are filled when there is no scheduled teaching.'
      }
    },
    {
      type: DashboardComponentType.TimeSeries,
      name: 'utilization-rate-time-series-booked',
      measurements: ['utilization.used_booked', 'utilization.utilization_rate_used_booked'],
      yAxisTicks: {
        type: 'auto',
        start: 0,
        minimumEnd: 1
      },
      title: {
        sv: 'Nyttjandegrad per tidsenhet',
        en: 'Utilization rate per time unit'
      },
      info: {
        sv: 'Grafen visar vald användning och nyttjandegrad över tid. \n\n OBS: Användningen i detta diagram visas endast på rum med personräknare och kan således skilja sig från användningen i analysen ”Användning och Bokning”. \n\n \nMätvärden:\n- Användning: Hur stor del av totalt tillgängliga timmar som är använda\n-Använd bokad: Hur stor del av tiden som är bokad och använd. \n- Använd ej bokad: Hur stor del av tiden det är använt men ej bokad. \n- Nyttjandegrad total tid: Visar hur väl tillgängliga platser nyttjas per rum under allt tillgänglig tid. \n- Nyttjandegrad bokad tid: Visar hur nyttjandegraden är i rummen när dom är använda och bokade. Indikerar hur väl storlek på rum matchar verksamhetens behov. \n- Nyttjandegrad ej bokad tid: Visar hur nyttjandegraden är i rummen när dom är använda men ej bokade. Indikerar hur väl rummen fylls när det inte är schemalagd undervisning.',
        en: 'The chart shows selected usage and utilization rate over time. \n\n Note: The usage in this chart is only shown for rooms with people counters and may therefore differ from the usage in the "Usage and Booking" analysis. \n\n \nMetrics:\n- Usage: The proportion of total available hours that are used\n- Used booked: The proportion of time that is booked and used. \n- Used not booked: The proportion of time that is used but not booked. \n- Utilization rate total time: Shows how well available seats are utilized per room during all available time. \n- Utilization rate booked time: Shows the utilization rate in the rooms when they are used and booked. Indicates how well the size of the room matches the needs of the activity. \n- Utilization rate not booked time: Shows the utilization rate in the rooms when they are used but not booked. Indicates how well the rooms are filled when there is no scheduled teaching.'
      }
    },
    {
      type: DashboardComponentType.BarChart,
      name: 'utilization-rate-bar-chart-hours-booked',
      grouping: 'grouped',
      measurements: ['utilization.used_booked', 'utilization.utilization_rate_used_booked'],
      groupKey: 'timestamp_extract_hour',
      codomainTicks: {
        type: 'auto',
        minimumEnd: 1
      },
      measurementOverride: {
        options: {
          colorScale: null
        }
      },
      filters: {
        values: {
          query: {
            groupBy: [{ type: 'timestamp_extract', extract: 'HOUR' }]
          }
        }
      },
      title: {
        sv: 'Nyttjandegrad över timmar',
        en: 'Utilization rate over hours'
      },
      info: {
        sv: 'Stapeldiagrammet visar vald typ av användning och nyttjandegrad aggregerat över timmar. \n\n OBS: Användningen i detta diagram visas endast på rum med personräknare och kan således skilja sig från användningen i analysen ”Användning och Bokning” \n\n \nMätvärden:\n- Användning: Hur stor del av totalt tillgängliga timmar som är använda\n-Använd bokad: Hur stor del av tiden som är bokad och använd. \n- Använd ej bokad: Hur stor del av tiden det är använt men ej bokad. \n- Nyttjandegrad total tid: Visar hur väl tillgängliga platser nyttjas per rum under allt tillgänglig tid. \n- Nyttjandegrad bokad tid: Visar hur nyttjandegraden är i rummen när dom är använda och bokade. Indikerar hur väl storlek på rum matchar verksamhetens behov. \n- Nyttjandegrad ej bokad tid: Visar hur nyttjandegraden är i rummen när dom är använda men ej bokade. Indikerar hur väl rummen fylls när det inte är schemalagd undervisning.',
        en: 'The bar chart shows selected usage and utilization rate aggregated over hours. \n\n Note: The usage in this chart is only shown for rooms with people counters and may therefore differ from the usage in the "Usage and Booking" analysis. \n\n \nMetrics:\n- Usage: The proportion of total available hours that are used\n- Used booked: The proportion of time that is booked and used. \n- Used not booked: The proportion of time that is used but not booked. \n- Utilization rate total time: Shows how well available seats are utilized per room during all available time. \n- Utilization rate booked time: Shows the utilization rate in the rooms when they are used and booked. Indicates how well the size of the room matches the needs of the activity. \n- Utilization rate not booked time: Shows the utilization rate in the rooms when they are used but not booked. Indicates how well the rooms are filled when there is no scheduled teaching.'
      }
    },
    {
      type: DashboardComponentType.BarChart,
      name: 'utilization-rate-bar-chart-booked',
      grouping: 'grouped',
      measurements: ['utilization.used_booked', 'utilization.utilization_rate_used_booked'],
      groupKey: 'timestamp_extract_isodow',
      codomainTicks: {
        type: 'auto',
        minimumEnd: 1
      },
      measurementOverride: {
        options: {
          colorScale: null
        }
      },
      filters: {
        values: {
          query: {
            groupBy: [{ type: 'timestamp_extract', extract: 'ISODOW' }]
          }
        }
      },
      title: {
        sv: 'Nyttjandegrad över veckodagar',
        en: 'Utilization rate over weekdays'
      },
      info: {
        sv: 'Stapeldiagrammet visar vald typ av användning och nyttjandegrad aggregerat över veckodag. \n\n OBS: Användningen i detta diagram visas endast på rum med personräknare och kan således skilja sig från användningen i analysen ”Användning och Bokning” \n\n \nMätvärden:\n- Användning: Hur stor del av totalt tillgängliga timmar som är använda\n-Använd bokad: Hur stor del av tiden som är bokad och använd. \n- Använd ej bokad: Hur stor del av tiden det är använt men ej bokad. \n- Nyttjandegrad total tid: Visar hur väl tillgängliga platser nyttjas per rum under allt tillgänglig tid. \n- Nyttjandegrad bokad tid: Visar hur nyttjandegraden är i rummen när dom är använda och bokade. Indikerar hur väl storlek på rum matchar verksamhetens behov. \n- Nyttjandegrad ej bokad tid: Visar hur nyttjandegraden är i rummen när dom är använda men ej bokade. Indikerar hur väl rummen fylls när det inte är schemalagd undervisning.',
        en: 'The bar chart shows the selected type of usage and utilization rate aggregated over weekdays. \n\n Note: The usage in this chart is only shown for rooms with people counters and may therefore differ from the usage in the "Usage and Booking" analysis. \n\n \nMetrics:\n- Usage: The proportion of total available hours that are used\n- Used booked: The proportion of time that is booked and used. \n- Used not booked: The proportion of time that is used but not booked. \n- Utilization rate total time: Shows how well available seats are utilized per room during all available time. \n- Utilization rate booked time: Shows the utilization rate in the rooms when they are used and booked. Indicates how well the size of the room matches the needs of the activity. \n- Utilization rate not booked time: Shows how well the rooms are filled when there is no scheduled teaching.'
      }
    },
    {
      type: DashboardComponentType.BarChart,
      name: 'utilization-rate-bar-chart_booked-number-of-seats',
      grouping: 'grouped',
      measurements: ['utilization.used_booked', 'utilization.utilization_rate_used_booked'],
      groupKey: 'width_bucket_number_of_seats',
      codomainTicks: {
        type: 'auto',
        minimumEnd: 1
      },
      measurementOverride: {
        options: {
          colorScale: null
        }
      },
      filters: {
        values: {
          query: {
            groupBy: [
              { type: 'width_bucket', min: 0, max: 100, count: 4, column: 'number_of_seats' }
            ]
          }
        }
      },
      title: {
        sv: 'Nyttjandegrad per antal platser',
        en: 'Utilization rate per number of seats'
      },
      info: {
        sv: 'Stapeldiagrammet visar vald typ av användning och nyttjandegrad aggregerat per antal platser. \n\n OBS: Användningen i detta diagram visas endast på rum med personräknare och kan således skilja sig från användningen i analysen ”Användning och Bokning”. \n\n \nMätvärden:\n- Användning: Hur stor del av totalt tillgängliga timmar som är använda\n-Använd bokad: Hur stor del av tiden som är bokad och använd. \n- Använd ej bokad: Hur stor del av tiden det är använt men ej bokad. \n- Nyttjandegrad total tid: Visar hur väl tillgängliga platser nyttjas per rum under allt tillgänglig tid. \n- Nyttjandegrad bokad tid: Visar hur nyttjandegraden är i rummen när dom är använda och bokade. Indikerar hur väl storlek på rum matchar verksamhetens behov. \n- Nyttjandegrad ej bokad tid: Visar hur nyttjandegraden är i rummen när dom är använda men ej bokade. Indikerar hur väl rummen fylls när det inte är schemalagd undervisning.',
        en: 'The bar chart shows selected usage and utilization rate aggregated per number of seats. \n\n Note: The usage in this chart is only shown for rooms with people counters and may therefore differ from the usage in the "Usage and Booking" analysis. \n\n \nMetrics:\n- Usage: The proportion of total available hours that are used\n- Used booked: The proportion of time that is booked and used. \n- Used not booked: The proportion of time that is used but not booked. \n- Utilization rate total time: Shows how well available seats are utilized per room during all available time. \n- Utilization rate booked time: Shows the utilization rate in the rooms when they are used and booked. Indicates how well the size of the room matches the needs of the activity. \n- Utilization rate not booked time: Shows the utilization rate in the rooms when they are used but not booked. Indicates how well the rooms are filled when there is no scheduled teaching.'
      }
    },
    {
      type: DashboardComponentType.TimeSeries,
      name: 'utilization-rate-time-series-not-booked',
      measurements: ['utilization.used_not_booked', 'utilization.utilization_rate_used_not_booked'],
      yAxisTicks: {
        type: 'auto',
        start: 0,
        minimumEnd: 1
      },
      title: {
        sv: 'Nyttjandegrad per tidsenhet',
        en: 'Utilization rate per time unit'
      },
      info: {
        sv: 'Grafen visar vald användning och nyttjandegrad över tid. \n\n OBS: Användningen i detta diagram visas endast på rum med personräknare och kan således skilja sig från användningen i analysen ”Användning och Bokning”. \n\n \nMätvärden:\n- Användning: Hur stor del av totalt tillgängliga timmar som är använda\n-Använd bokad: Hur stor del av tiden som är bokad och använd. \n- Använd ej bokad: Hur stor del av tiden det är använt men ej bokad. \n- Nyttjandegrad total tid: Visar hur väl tillgängliga platser nyttjas per rum under allt tillgänglig tid. \n- Nyttjandegrad bokad tid: Visar hur nyttjandegraden är i rummen när dom är använda och bokade. Indikerar hur väl storlek på rum matchar verksamhetens behov. \n- Nyttjandegrad ej bokad tid: Visar hur nyttjandegraden är i rummen när dom är använda men ej bokade. Indikerar hur väl rummen fylls när det inte är schemalagd undervisning.',
        en: 'The chart shows selected usage and utilization rate over time. \n\n Note: The usage in this chart is only shown for rooms with people counters and may therefore differ from the usage in the "Usage and Booking" analysis. \n\n \nMetrics:\n- Usage: The proportion of total available hours that are used\n- Used booked: The proportion of time that is booked and used. \n- Used not booked: The proportion of time that is used but not booked. \n- Utilization rate total time: Shows how well available seats are utilized per room during all available time. \n- Utilization rate booked time: Shows the utilization rate in the rooms when they are used and booked. Indicates how well the size of the room matches the needs of the activity. \n- Utilization rate not booked time: Shows the utilization rate in the rooms when they are used but not booked. Indicates how well the rooms are filled when there is no scheduled teaching.'
      }
    },
    {
      type: DashboardComponentType.BarChart,
      name: 'utilization-rate-bar-chart-not-booked',
      grouping: 'grouped',
      measurements: ['utilization.used_not_booked', 'utilization.utilization_rate_used_not_booked'],
      groupKey: 'timestamp_extract_isodow',
      codomainTicks: {
        type: 'auto',
        minimumEnd: 1
      },
      measurementOverride: {
        options: {
          colorScale: null
        }
      },
      filters: {
        values: {
          query: {
            groupBy: [{ type: 'timestamp_extract', extract: 'ISODOW' }]
          }
        }
      },
      title: {
        sv: 'Nyttjandegrad över veckodagar',
        en: 'Utilization rate over weekdays'
      },
      info: {
        sv: 'Stapeldiagrammet visar vald typ av användning och nyttjandegrad aggregerat över veckodag. \n\n OBS: Användningen i detta diagram visas endast på rum med personräknare och kan således skilja sig från användningen i analysen ”Användning och Bokning” \n\n \nMätvärden:\n- Användning: Hur stor del av totalt tillgängliga timmar som är använda\n-Använd bokad: Hur stor del av tiden som är bokad och använd. \n- Använd ej bokad: Hur stor del av tiden det är använt men ej bokad. \n- Nyttjandegrad total tid: Visar hur väl tillgängliga platser nyttjas per rum under allt tillgänglig tid. \n- Nyttjandegrad bokad tid: Visar hur nyttjandegraden är i rummen när dom är använda och bokade. Indikerar hur väl storlek på rum matchar verksamhetens behov. \n- Nyttjandegrad ej bokad tid: Visar hur nyttjandegraden är i rummen när dom är använda men ej bokade. Indikerar hur väl rummen fylls när det inte är schemalagd undervisning.',
        en: 'The bar chart shows the selected type of usage and utilization rate aggregated over weekdays. \n\n Note: The usage in this chart is only shown for rooms with people counters and may therefore differ from the usage in the "Usage and Booking" analysis. \n\n \nMetrics:\n- Usage: The proportion of total available hours that are used\n- Used booked: The proportion of time that is booked and used. \n- Used not booked: The proportion of time that is used but not booked. \n- Utilization rate total time: Shows how well available seats are utilized per room during all available time. \n- Utilization rate booked time: Shows the utilization rate in the rooms when they are used and booked. Indicates how well the size of the room matches the needs of the activity. \n- Utilization rate not booked time: Shows how well the rooms are filled when there is no scheduled teaching.'
      }
    },
    {
      type: DashboardComponentType.BarChart,
      name: 'utilization-rate-bar-chart-hours-not-booked',
      grouping: 'grouped',
      measurements: ['utilization.used_not_booked', 'utilization.utilization_rate_used_not_booked'],
      groupKey: 'timestamp_extract_hour',
      codomainTicks: {
        type: 'auto',
        minimumEnd: 1
      },
      measurementOverride: {
        options: {
          colorScale: null
        }
      },
      filters: {
        values: {
          query: {
            groupBy: [{ type: 'timestamp_extract', extract: 'HOUR' }]
          }
        }
      },
      title: {
        sv: 'Nyttjandegrad per tidsenhet',
        en: 'Utilization rate per time unit'
      },
      info: {
        sv: 'Grafen visar vald användning och nyttjandegrad över tid. \n\n OBS: Användningen i detta diagram visas endast på rum med personräknare och kan således skilja sig från användningen i analysen ”Användning och Bokning”. \n\n \nMätvärden:\n- Användning: Hur stor del av totalt tillgängliga timmar som är använda\n-Använd bokad: Hur stor del av tiden som är bokad och använd. \n- Använd ej bokad: Hur stor del av tiden det är använt men ej bokad. \n- Nyttjandegrad total tid: Visar hur väl tillgängliga platser nyttjas per rum under allt tillgänglig tid. \n- Nyttjandegrad bokad tid: Visar hur nyttjandegraden är i rummen när dom är använda och bokade. Indikerar hur väl storlek på rum matchar verksamhetens behov. \n- Nyttjandegrad ej bokad tid: Visar hur nyttjandegraden är i rummen när dom är använda men ej bokade. Indikerar hur väl rummen fylls när det inte är schemalagd undervisning.',
        en: 'The chart shows selected usage and utilization rate over time. \n\n Note: The usage in this chart is only shown for rooms with people counters and may therefore differ from the usage in the "Usage and Booking" analysis. \n\n \nMetrics:\n- Usage: The proportion of total available hours that are used\n- Used booked: The proportion of time that is booked and used. \n- Used not booked: The proportion of time that is used but not booked. \n- Utilization rate total time: Shows how well available seats are utilized per room during all available time. \n- Utilization rate booked time: Shows the utilization rate in the rooms when they are used and booked. Indicates how well the size of the room matches the needs of the activity. \n- Utilization rate not booked time: Shows the utilization rate in the rooms when they are used but not booked. Indicates how well the rooms are filled when there is no scheduled teaching.'
      }
    },
    {
      type: DashboardComponentType.BarChart,
      name: 'utilization-rate-bar-chart-not-booked-number-of-seats',
      grouping: 'grouped',
      measurements: ['utilization.used_not_booked', 'utilization.utilization_rate_used_not_booked'],
      groupKey: 'width_bucket_number_of_seats',
      codomainTicks: {
        type: 'auto',
        minimumEnd: 1
      },
      measurementOverride: {
        options: {
          colorScale: null
        }
      },
      filters: {
        values: {
          query: {
            groupBy: [
              { type: 'width_bucket', min: 0, max: 100, count: 4, column: 'number_of_seats' }
            ]
          }
        }
      },
      title: {
        sv: 'Nyttjandegrad per antal platser',
        en: 'Utilization rate per number of seats'
      },
      info: {
        sv: 'Stapeldiagrammet visar vald typ av användning och nyttjandegrad fördelar sig beroende på storlek på rum. \n\n OBS: Användningen i detta diagram visas endast på rum med personräknare och kan således skilja sig från användningen i analysen ”Användning och Bokning”. \n\n \nMätvärden:\n- Användning: Hur stor del av totalt tillgängliga timmar som är använda\n-Använd bokad: Hur stor del av tiden som är bokad och använd. \n- Använd ej bokad: Hur stor del av tiden det är använt men ej bokad. \n- Nyttjandegrad total tid: Visar hur väl tillgängliga platser nyttjas per rum under allt tillgänglig tid. \n- Nyttjandegrad bokad tid: Visar hur nyttjandegraden är i rummen när dom är använda och bokade. Indikerar hur väl storlek på rum matchar verksamhetens behov. \n- Nyttjandegrad ej bokad tid: Visar hur nyttjandegraden är i rummen när dom är använda men ej bokade. Indikerar hur väl rummen fylls när det inte är schemalagd undervisning.',
        en: 'The bar chart shows selected usage and utilization rate distributing depending on room size. \n\n Note: The usage in this chart is only shown for rooms with people counters and may therefore differ from the usage in the "Usage and Booking" analysis. \n\n \nMetrics:\n- Usage: The proportion of total available hours that are used\n- Used booked: The proportion of time that is booked and used. \n- Used not booked: The proportion of time that is used but not booked. \n- Utilization rate total time: Shows how well available seats are utilized per room during all available time. \n- Utilization rate booked time: Shows the utilization rate in the rooms when they are used and booked. Indicates how well the size of the room matches the needs of the activity. \n- Utilization rate not booked time: Shows the utilization rate in the rooms when they are used but not booked. Indicates how well the rooms are filled when there is no scheduled teaching.'
      }
    },
    {
      type: DashboardComponentType.Toplist,
      name: 'utilization-rate-top-list',
      grouping: 'grouped',
      measurements: ['utilization.usage', 'utilization.utilization_rate'],
      filters: {
        values: {
          query: {
            groupBy: [{ type: 'path_subtree' }],
            query: {
              orderBy: [{ field: 'usage', direction: 'DESC', nulls: 'LAST' }],
              limit: 1000
            }
          }
        }
      },
      title: {
        sv: 'Nyttjandegrad per entitet',
        en: 'Utilization rate per entity'
      },
      info: {
        sv: 'Stapeldiagrammet visar vald typ av användning och nyttjandegrad per entitet. \n\n OBS: Användningen i detta diagram visas endast på rum med personräknare och kan således skilja sig från användningen i analysen ”Användning och Bokning” \n\n \nMätvärden:\n- Användning: Hur stor del av totalt tillgängliga timmar som är använda\n-Använd bokad: Hur stor del av tiden som är bokad och använd. \n- Använd ej bokad: Hur stor del av tiden det är använt men ej bokad. \n- Nyttjandegrad total tid: Visar hur väl tillgängliga platser nyttjas per rum under allt tillgänglig tid. \n- Nyttjandegrad bokad tid: Visar hur nyttjandegraden är i rummen när dom är använda och bokade. Indikerar hur väl storlek på rum matchar verksamhetens behov. \n- Nyttjandegrad ej bokad tid: Visar hur nyttjandegraden är i rummen när dom är använda men ej bokade. Indikerar hur väl rummen fylls när det inte är schemalagd undervisning.',
        en: 'The bar chart shows selected usage and utilization rate per entity. \n\n Note: The usage in this chart is only shown for rooms with people counters and may therefore differ from the usage in the "Usage and Booking" analysis. \n\n \nMetrics:\n- Usage: The proportion of total available hours that are used\n- Used booked: The proportion of time that is booked and used. \n- Used not booked: The proportion of time that is used but not booked. \n- Utilization rate total time: Shows how well available seats are utilized per room during all available time. \n- Utilization rate booked time: Shows the utilization rate in the rooms when they are used and booked. Indicates how well the size of the room matches the needs of the activity. \n- Utilization rate not booked time: Shows the utilization rate in the rooms when they are used but not booked. Indicates how well the rooms are filled when there is no scheduled teaching.'
      }
    },
    {
      type: DashboardComponentType.Toplist,
      name: 'utilization-rate-top-list-booked',
      grouping: 'grouped',
      measurements: ['utilization.used_booked', 'utilization.utilization_rate_used_booked'],
      filters: {
        values: {
          query: {
            groupBy: [{ type: 'path_subtree' }],
            query: {
              orderBy: [{ field: 'used_booked', direction: 'DESC', nulls: 'LAST' }],
              limit: 1000
            }
          }
        }
      },
      title: {
        sv: 'Nyttjandegrad per entitet',
        en: 'Utilization rate per entity'
      },
      info: {
        sv: 'Stapeldiagrammet visar vald typ av användning och nyttjandegrad per entitet. \n\n OBS: Användningen i detta diagram visas endast på rum med personräknare och kan således skilja sig från användningen i analysen ”Användning och Bokning” \n\n \nMätvärden:\n- Användning: Hur stor del av totalt tillgängliga timmar som är använda\n-Använd bokad: Hur stor del av tiden som är bokad och använd. \n- Använd ej bokad: Hur stor del av tiden det är använt men ej bokad. \n- Nyttjandegrad total tid: Visar hur väl tillgängliga platser nyttjas per rum under allt tillgänglig tid. \n- Nyttjandegrad bokad tid: Visar hur nyttjandegraden är i rummen när dom är använda och bokade. Indikerar hur väl storlek på rum matchar verksamhetens behov. \n- Nyttjandegrad ej bokad tid: Visar hur nyttjandegraden är i rummen när dom är använda men ej bokade. Indikerar hur väl rummen fylls när det inte är schemalagd undervisning.',
        en: 'The bar chart shows selected usage and utilization rate per entity. \n\n Note: The usage in this chart is only shown for rooms with people counters and may therefore differ from the usage in the "Usage and Booking" analysis. \n\n \nMetrics:\n- Usage: The proportion of total available hours that are used\n- Used booked: The proportion of time that is booked and used. \n- Used not booked: The proportion of time that is used but not booked. \n- Utilization rate total time: Shows how well available seats are utilized per room during all available time. \n- Utilization rate booked time: Shows the utilization rate in the rooms when they are used and booked. Indicates how well the size of the room matches the needs of the activity. \n- Utilization rate not booked time: Shows the utilization rate in the rooms when they are used but not booked. Indicates how well the rooms are filled when there is no scheduled teaching.'
      }
    },
    {
      type: DashboardComponentType.Toplist,
      name: 'utilization-rate-top-list-not-booked',
      grouping: 'grouped',
      measurements: ['utilization.used_not_booked', 'utilization.utilization_rate_used_not_booked'],
      filters: {
        values: {
          query: {
            groupBy: [{ type: 'path_subtree' }],
            query: {
              orderBy: [{ field: 'used_not_booked', direction: 'DESC', nulls: 'LAST' }],
              limit: 1000
            }
          }
        }
      },
      title: {
        sv: 'Nyttjandegrad per entitet',
        en: 'Utilization rate per entity'
      },
      info: {
        sv: 'Stapeldiagrammet visar vald typ av användning och nyttjandegrad per entitet. \n\n OBS: Användningen i detta diagram visas endast på rum med personräknare och kan således skilja sig från användningen i analysen ”Användning och Bokning” \n\n \nMätvärden:\n- Användning: Hur stor del av totalt tillgängliga timmar som är använda\n-Använd bokad: Hur stor del av tiden som är bokad och använd. \n- Använd ej bokad: Hur stor del av tiden det är använt men ej bokad. \n- Nyttjandegrad total tid: Visar hur väl tillgängliga platser nyttjas per rum under allt tillgänglig tid. \nNyttjandegrad bokad tid: Visar hur nyttjandegraden är i rummen när dom är använda och bokade. Indikerar hur väl storlek på rum matchar verksamhetens behov. \n- Nyttjandegrad ej bokad tid: Visar hur nyttjandegraden är i rummen när dom är använda men ej bokade. Indikerar hur väl rummen fylls när det inte är schemalagd undervisning.',
        en: 'The bar chart shows selected usage and utilization rate per entity. \n\n Note: The usage in this chart is only shown for rooms with people counters and may therefore differ from the usage in the "Usage and Booking" analysis. \n\n \nMetrics:\n- Usage: The proportion of total available hours that are used\n- Used booked: The proportion of time that is booked and used. \n- Used not booked: The proportion of time that is used but not booked. \n- Utilization rate total time: Shows how well available seats are utilized per room during all available time. \n Utilization rate booked time: Shows the utilization rate in the rooms when they are used and booked. Indicates how well the size of the room matches the needs of the activity. \n- Utilization rate not booked time: Shows the utilization rate in the rooms when they are used but not booked. Indicates how well the rooms are filled when there is no scheduled teaching.'
      }
    },
    {
      type: DashboardComponentType.Histogram,
      name: 'utilization-rate-number-of-hours-histogram',
      measurements: ['utilization_rate_histogram.utilization_rate_histogram'],
      title: {
        sv: 'Fördelning av nyttjandegrad för antal timmar',
        en: 'Distribution of utilization for number of hours'
      },
      info: {
        sv: 'Stapeldiagrammet visa hur många timmar vald typ av nyttjandegrad inträffar. \n\n \nMätvärden:\n- Användning: Hur stor del av totalt tillgängliga timmar som är använda\n-Använd bokad: Hur stor del av tiden som är bokad och använd. \n- Använd ej bokad: Hur stor del av tiden det är använt men ej bokad. \n- Nyttjandegrad total tid: Visar hur väl tillgängliga platser nyttjas per rum under allt tillgänglig tid. \nNyttjandegrad bokad tid: Visar hur nyttjandegraden är i rummen när dom är använda och bokade. Indikerar hur väl storlek på rum matchar verksamhetens behov. \n- Nyttjandegrad ej bokad tid: Visar hur nyttjandegraden är i rummen när dom är använda men ej bokade. Indikerar hur väl rummen fylls när det inte är schemalagd undervisning.',
        en: 'The bar chart shows how many hours the selected type of utilization rate occurs. \n\n \nMetrics:\n- Usage: The proportion of total available hours that are used\n- Used booked: The proportion of time that is booked and used. \n- Used not booked: The proportion of time that is used but not booked. \n- Utilization rate total time: Shows how well available seats are utilized per room during all available time. \n Utilization rate booked time: Shows the utilization rate in the rooms when they are used and booked. Indicates how well the size of the room matches the needs of the activity. \n- Utilization rate not booked time: Shows the utilization rate in the rooms when they are used but not booked. Indicates how well the rooms are filled when there is no scheduled teaching.'
      },
      normalize: false,
      showRanges: true
    },
    {
      type: DashboardComponentType.Histogram,
      name: 'utilization-rate-number-of-hours-histogram-used-booked',
      measurements: ['used_booked_histogram.used_booked_histogram'],
      title: {
        sv: 'Fördelning av nyttjandegrad för antal timmar',
        en: 'Distribution of utilization for number of hours'
      },
      info: {
        sv: 'Stapeldiagrammet visa hur många timmar vald typ av nyttjandegrad inträffar. \n\n \nMätvärden:\n- Användning: Hur stor del av totalt tillgängliga timmar som är använda\n-Använd bokad: Hur stor del av tiden som är bokad och använd. \n- Använd ej bokad: Hur stor del av tiden det är använt men ej bokad. \n- Nyttjandegrad total tid: Visar hur väl tillgängliga platser nyttjas per rum under allt tillgänglig tid. \nNyttjandegrad bokad tid: Visar hur nyttjandegraden är i rummen när dom är använda och bokade. Indikerar hur väl storlek på rum matchar verksamhetens behov. \n- Nyttjandegrad ej bokad tid: Visar hur nyttjandegraden är i rummen när dom är använda men ej bokade. Indikerar hur väl rummen fylls när det inte är schemalagd undervisning.',
        en: 'The bar chart shows how many hours the selected type of utilization rate occurs. \n\n \nMetrics:\n- Usage: The proportion of total available hours that are used\n- Used booked: The proportion of time that is booked and used. \n- Used not booked: The proportion of time that is used but not booked. \n- Utilization rate total time: Shows how well available seats are utilized per room during all available time. \n Utilization rate booked time: Shows the utilization rate in the rooms when they are used and booked. Indicates how well the size of the room matches the needs of the activity. \n- Utilization rate not booked time: Shows the utilization rate in the rooms when they are used but not booked. Indicates how well the rooms are filled when there is no scheduled teaching.'
      },
      normalize: false,
      showRanges: true
    },
    {
      type: DashboardComponentType.Histogram,
      name: 'utilization-rate-number-of-hours-histogram-used-not-booked',
      measurements: ['used_not_booked_histogram.used_not_booked_histogram'],
      title: {
        sv: 'Fördelning av nyttjandegrad för antal timmar',
        en: 'Distribution of utilization for number of hours'
      },
      info: {
        sv: 'Stapeldiagrammet visa hur många timmar vald typ av nyttjandegrad inträffar. \n\n \nMätvärden:\n- Användning: Hur stor del av totalt tillgängliga timmar som är använda\n-Använd bokad: Hur stor del av tiden som är bokad och använd. \n- Använd ej bokad: Hur stor del av tiden det är använt men ej bokad. \n- Nyttjandegrad total tid: Visar hur väl tillgängliga platser nyttjas per rum under allt tillgänglig tid. \nNyttjandegrad bokad tid: Visar hur nyttjandegraden är i rummen när dom är använda och bokade. Indikerar hur väl storlek på rum matchar verksamhetens behov. \n- Nyttjandegrad ej bokad tid: Visar hur nyttjandegraden är i rummen när dom är använda men ej bokade. Indikerar hur väl rummen fylls när det inte är schemalagd undervisning.',
        en: 'The bar chart shows how many hours the selected type of utilization rate occurs. \n\n \nMetrics:\n- Usage: The proportion of total available hours that are used\n- Used booked: The proportion of time that is booked and used. \n- Used not booked: The proportion of time that is used but not booked. \n- Utilization rate total time: Shows how well available seats are utilized per room during all available time. \n Utilization rate booked time: Shows the utilization rate in the rooms when they are used and booked. Indicates how well the size of the room matches the needs of the activity. \n- Utilization rate not booked time: Shows the utilization rate in the rooms when they are used but not booked. Indicates how well the rooms are filled when there is no scheduled teaching.'
      },
      normalize: false,
      showRanges: true
    }
  ],
  tabs: [
    {
      name: 'utilization-rate-total',
      title: { sv: 'Nyttjandegrad (total)', en: 'Utilization Rate (total)' },
      components: [
        'used-utilization-rate',
        'utilization_rate-utilization-rate',
        'used-booked-utilization',
        'utilization_rate_used_booked-utilization-rate',
        'used-not-booked-utilization',
        'used_not_booked-utilization-rate',
        'utilization-rate-time-series',
        'utilization-rate-bar-chart',
        'utilization-rate-bar-chart-hours',
        'utilization-rate-bar-chart-number-of-seats',
        'utilization-rate-number-of-hours-histogram',
        'utilization-rate-top-list'
      ],
      initialShowOnMapComponent: 'used-utilization-rate'
    },
    {
      name: 'utilization-rate-used-booked',
      title: { sv: 'Använd, bokad', en: 'Used, booked' },
      components: [
        'used-booked-utilization',
        'utilization_rate_used_booked-utilization-rate',
        'utilization-rate-time-series-booked',
        'utilization-rate-bar-chart-booked',
        'utilization-rate-bar-chart-hours-booked',
        'utilization-rate-bar-chart_booked-number-of-seats',
        'utilization-rate-number-of-hours-histogram-used-booked',
        'utilization-rate-top-list-booked'
      ],
      initialShowOnMapComponent: 'used-booked-utilization'
    },
    {
      name: 'utilization-rate-used-not-booked',
      title: { sv: 'Använd, ej bokad', en: 'Used, not booked' },
      components: [
        'used-not-booked-utilization',
        'used_not_booked-utilization-rate',
        'utilization-rate-time-series-not-booked',
        'utilization-rate-bar-chart-not-booked',
        'utilization-rate-bar-chart-hours-not-booked',
        'utilization-rate-bar-chart-not-booked-number-of-seats',
        'utilization-rate-number-of-hours-histogram-used-not-booked',
        'utilization-rate-top-list-not-booked'
      ],
      initialShowOnMapComponent: 'used-not-booked-utilization'
    }
  ]
})
